import { Color4, Engine, EngineStore, Scene, Texture, Vector2 } from '@babylonjs/core';

import { BMFont, BMFontChar } from './BMFont';
import { SdfFontSimpleMaterial } from './SdfFontSimpleMaterial';

export type SdfFontDistanceField = {
  fieldType: 'sdf' | 'msdf';
  distanceRange: number;
};

export type SdfFont = BMFont & {
  distanceField: SdfFontDistanceField;
};

export enum CharCode {
  SPACE = 32,
  TOFU = 0xfffc,
}

export class SdfFontAsset {
  static readonly registered = new Map<string, SdfFontAsset>();
  static From(font: SdfFont, scene: Scene) {
    const name = font.info.face;
    const existing = SdfFontAsset.registered.get(name);
    return existing ?? new SdfFontAsset(font, scene);
  }

  private readonly chars = new Map<number, BMFontChar>();
  private readonly charsRegex;

  private readonly kernings = new Map<number, Map<number, number>>();

  public readonly scale: number;
  public readonly textures: Texture[];
  public readonly material: SdfFontSimpleMaterial;

  private constructor(
    public readonly font: SdfFont,
    scene: Scene
  ) {
    const name = font.info.face;

    font.chars.forEach((char) => this.chars.set(char.id, char));
    font.kernings.forEach((kerning) => {
      let submap = this.kernings.get(kerning.first);
      if (!submap) {
        submap = new Map();
        this.kernings.set(kerning.first, submap);
      }
      submap.set(kerning.second, kerning.amount);
    });
    this.charsRegex = new RegExp(
      `[${font.chars.map((c) => c.char.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')).join('')}]`,
      'g'
    );

    this.updateFallbacks();

    const textureSize = new Vector2(this.font.common.scaleW, this.font.common.scaleH);

    this.scale = 1 / this.font.info.size;
    this.textures = this.font.pages.map((page) => new Texture(page, undefined, { noMipmap: true, invertY: false }));
    this.material = new SdfFontSimpleMaterial(name, scene);
    this.material.setTexture('fontAtlas', this.textures[0]!);
    this.material.setVector2(
      'unitRange',
      Vector2.One().scaleInPlace(this.font.distanceField.distanceRange).divideInPlace(textureSize)
    );
    this.material.setVector2('texelSize', Vector2.One().divideInPlace(textureSize));
    this.material.setColor4('uColor', Color4.FromInts(255, 255, 255, 255));
    this.material.onDisposeObservable.add(() => {
      SdfFontAsset.registered.delete(name);
    });

    SdfFontAsset.registered.set(name, this);
  }

  updateFallbacks() {
    if (!this.chars.has(CharCode.SPACE)) {
      this.chars.set(CharCode.SPACE, {
        id: CharCode.SPACE,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        xoffset: 0,
        yoffset: 0,
        xadvance: this.font.info.size * 0.5,
        page: -1,
        chnl: -1,
        index: -1,
        char: ' ',
      });
    }

    if (!this.chars.has(CharCode.TOFU)) {
      this.chars.set(CharCode.TOFU, {
        id: CharCode.TOFU,
        x: 0,
        y: 0,
        width: this.font.info.size,
        height: this.font.info.size,
        xoffset: 0,
        yoffset: 0,
        xadvance: this.font.info.size * 0.5,
        page: -1,
        chnl: -1,
        index: -1,
        char: '￿',
      });
    }
  }

  getChar(charCode: number) {
    return this.chars.get(charCode) || this.chars.get(CharCode.TOFU)!;
  }

  getKerning(first: number, second: number) {
    return this.kernings.get(first)?.get(second) || 0;
  }

  unsupportedChars(text: string) {
    return text.replace(this.charsRegex, '');
  }
}
